/**
 * React Error Boundary component.
 *
 * Catches JavaScript errors in the component tree, logs them, and displays a
 * fallback UI instead of crashing the entire application.
 *
 * NOTE: This MUST be a class component. React Error Boundaries require the use
 * of getDerivedStateFromError and componentDidCatch lifecycle methods, which
 * are only available in class components. There is no functional/hooks
 * equivalent.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */

import React, { Component, ReactNode } from 'react'
import { datadogRum } from '@datadog/browser-rum'

/** Props for the ErrorBoundary component. */
export interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode
  /** Custom fallback UI (optional) */
  fallback?: ReactNode
  /** Callback when error is caught (optional) */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

/** State for the ErrorBoundary component. */
interface ErrorBoundaryState {
  /** Whether an error has been caught */
  hasError: boolean
  /** The caught error (if any) */
  error: Error | null
}

/**
 * ErrorBoundary component for catching and handling React errors.
 *
 * Wraps components to catch errors in their render methods, lifecycle methods,
 * and constructors. Displays a fallback UI (or nothing if no fallback provided)
 * and prevents the entire app from crashing.
 *
 * @example
 *   // With custom fallback UI
 *   ;<ErrorBoundary fallback={<div>Error occurred</div>}>
 *     <MyComponent />
 *   </ErrorBoundary>
 *
 * @example
 *   // Silent error catching (no UI, just error callback)
 *   ;<ErrorBoundary
 *     fallback={null}
 *     onError={error => console.error('Caught:', error)}
 *   >
 *     <MyComponent />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  /**
   * Static method called when an error is caught.
   *
   * Updates state to trigger fallback UI rendering.
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    }
  }

  /**
   * Lifecycle method called after an error is caught.
   *
   * Logs the error and calls the optional onError callback.
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught error:', error, errorInfo)
    }

    datadogRum.addError(error, {
      source: 'react-error-boundary',
      componentStack: errorInfo.componentStack,
    })

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  render(): ReactNode {
    // If error caught, render fallback UI (or nothing if no fallback provided)
    if (this.state.hasError) {
      // Check if fallback prop was explicitly provided (including null)
      if ('fallback' in this.props) {
        return this.props.fallback
      }

      // No fallback provided and no default - just render nothing
      return null
    }

    // No error, render children normally
    return this.props.children
  }
}
